import { atom } from 'jotai'
import { selectAtom } from 'jotai/utils'

import type { SidepanelChat } from './running-chat-selection'

export type ForYouLingerKind = 'archived' | 'executed'

export interface ForYouLingerEntry {
  chat: SidepanelChat
  kind: ForYouLingerKind
  /**
   * The feed the row was lingered on (`ForYouChatsResult.feedKey`). Only that feed holds
   * the row in place — a meeting's feed never held what For You archived. Hiding a spent
   * row applies to every feed.
   */
  feedKey: string
  actionId?: string
  position: number
  startedAt: number
  /**
   * Set once the hold has run. The row stays hidden from then on: the feed
   * usually still lists an executed conversation, so forgetting the entry here would
   * snap the row back to full opacity until the next refetch. New activity on the
   * conversation clears the entry (see `getReactivatedForYouLingerIds`).
   */
  elapsed?: boolean
  /**
   * Ticked in To-dos, so it leaves on that list's shared countdown rather than its own clock, and
   * the archive isn't sent until then.
   */
  sharesTodosCountdown?: boolean
}

export const forYouLingerAtom = atom(new Map<string, ForYouLingerEntry>())

export const sharedCountdownChatIdsAtom = selectAtom(
  forYouLingerAtom,
  (entries): ReadonlySet<string> =>
    new Set(
      [...entries.values()].filter((entry) => entry.sharesTodosCountdown && !entry.elapsed).map(({ chat }) => chat.id),
    ),
  (a, b) => a.size === b.size && [...a].every((id) => b.has(id)),
)

export const retireSharedCountdownChatsAtom = atom(null, (_get, set, ids: ReadonlySet<string>) =>
  set(forYouLingerAtom, (previous) => {
    let next: Map<string, ForYouLingerEntry> | null = null
    for (const [id, entry] of previous) {
      if (!entry.sharesTodosCountdown || entry.elapsed || !ids.has(id)) continue
      next ??= new Map(previous)
      next.set(id, { ...entry, elapsed: true })
    }
    return next ?? previous
  }),
)
